import { useEffect, useState } from "react";
import { useRecentSamples } from "../api";
import { useParamsContext, useUserContext, useAdminContext } from "../contexts";
import Footer from "../components/Footer";
import Navbar from "../components/SpectraNavbar";
import DateSelector from "../components/controls/DateSelector";
import LocationDropdown from "../components/controls/LocationDropdown";
import { CardA, CardC } from "../components/ui/Cards";
import { SequenceD } from "../components/ui/Sequence";
import { ColourCircleC } from "../components/ui/ColourCircle";
import { DateTime } from "luxon";
import { CSVLink } from "react-csv";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { FilterMatchMode, FilterService } from "primereact/api";
import { Tooltip } from "primereact/tooltip";

FilterService.register("equalsIgnoreCase", (value, filter) => {
  if (filter === null || filter === undefined) return true;
  if (value === null || value === undefined) return false;
  return value.toLowerCase() === filter.toLowerCase();
});

const headers = {
  "harm-reduction": [
    { label: "Sample ID", key: "sampleId" },
    { label: "Custom ID", key: "customId" },
    { label: "Location", key: "location" },
    { label: "Date", key: "processedDate" },
    { label: "Time", key: "time" },
    { label: "Presumed Substance", key: "presumedSubstance" },
    { label: "Colour", key: "colourName" },
    { label: "Texture", key: "textureName" },
    { label: "Bulk Scan", key: "bulkScanExport" },
    { label: "Trace Scan", key: "traceScanExport" },
    { label: "Adverse Reaction", key: "adverseReaction" },
    { label: "Adverse Reaction Symptoms", key: "adverseReactionSymptoms" },
    {
      label: "Adverse Reaction Intervention",
      key: "adverseReactionIntervention",
    },
    { label: "Scan Timing", key: "tested" },
    { label: "Consumption Location", key: "consumptionLocation" },
    { label: "Behaviour Change", key: "behaviourChange" },
    { label: "Consumption Method", key: "consumptionMethod" },
    { label: "Notes", key: "notes" },
    { label: "Other Analytical Results", key: "otherResults" },
  ],
  "law-enforcement": [
    { label: "Sample ID", key: "sampleId" },
    { label: "Custom ID", key: "customId" },
    { label: "Location", key: "location" },
    { label: "Date", key: "processedDate" },
    { label: "Time", key: "time" },
    { label: "Presumed Substance", key: "presumedSubstance" },
    { label: "Colour", key: "colourName" },
    { label: "Texture", key: "textureName" },
    { label: "Bulk Scan", key: "bulkScanExport" },
    { label: "Trace Scan", key: "traceScanExport" },
    { label: "Notes", key: "notes" },
    { label: "Other Analytical Results", key: "otherResults" },
  ],
  admin: [
    { label: "Sample ID", key: "sampleId" },
    { label: "Custom ID", key: "customId" },
    { label: "Customer Name", key: "customer" },
    { label: "Location", key: "location" },
    { label: "Date", key: "processedDate" },
    { label: "Time", key: "time" },
    { label: "Presumed Substance", key: "presumedSubstance" },
    { label: "Colour", key: "colourName" },
    { label: "Texture", key: "textureName" },
    { label: "Bulk Scan", key: "bulkScanExport" },
    { label: "Trace Scan", key: "traceScanExport" },
    { label: "Adverse Reaction", key: "adverseReaction" },
    { label: "Adverse Reaction Symptoms", key: "adverseReactionSymptoms" },
    {
      label: "Adverse Reaction Intervention",
      key: "adverseReactionIntervention",
    },
    { label: "Scan Timing", key: "tested" },
    { label: "Consumption Location", key: "consumptionLocation" },
    { label: "Behaviour Change", key: "behaviourChange" },
    { label: "Consumption Method", key: "consumptionMethod" },
    { label: "Notes", key: "notes" },
    { label: "Other Analytical Results", key: "otherResults" },
  ],
};

// Prevent CSV/Excel formula injection by prefixing dangerous leading characters.
function sanitizeCsvField(value) {
  if (typeof value === "string" && /^[=+\-@\t\r]/.test(value)) {
    return `\t${value}`;
  }
  return value;
}

function sanitizeRowForCsv(row) {
  return Object.fromEntries(
    Object.entries(row).map(([k, v]) => [k, sanitizeCsvField(v)]),
  );
}

export default function RecentSamples() {
  const [dateRange, setDateRange] = useState({
    start: DateTime.now().minus({ days: 6 }).toISODate(),
    end: DateTime.now().toISODate(),
  });
  const [selectedLocation, setSelectedLocation] = useState("All");

  const emptyFilters = {
    customer: { value: null, matchMode: FilterMatchMode.EQUALS },
    location: { value: null, matchMode: FilterMatchMode.EQUALS },
    sampleId: { value: null, matchMode: FilterMatchMode.CONTAINS },
    customId: { value: null, matchMode: FilterMatchMode.CONTAINS },
    presumedSubstance: { value: null, matchMode: "equalsIgnoreCase" },
    colourName: { value: null, matchMode: FilterMatchMode.EQUALS },
    textureName: { value: null, matchMode: "equalsIgnoreCase" },
    bulkScanExport: { value: null, matchMode: FilterMatchMode.CONTAINS },
    traceScanExport: { value: null, matchMode: FilterMatchMode.CONTAINS },
    reachbackStatusDisplay: { value: null, matchMode: FilterMatchMode.EQUALS },
    adverseReaction: { value: null, matchMode: FilterMatchMode.EQUALS },
  };
  const [filters, setFilters] = useState(emptyFilters);
  const hasActiveFilters = Object.values(filters).some((f) => f.value !== null);

  //contexts
  const { selectedCustomer, customers } = useAdminContext();
  const { getColour, getTexture, getAdverse } = useParamsContext();
  const { user } = useUserContext();
  const customerType = user?.customerType;
  const locations = user?.locID;

  //fetch recent samples
  const {
    data: recentSamples,
    isLoading,
    isSuccess,
    isError,
  } = useRecentSamples(dateRange, selectedLocation, selectedCustomer);

  const mapSample = (sample) => {
    const newItem = {};

    if (user.role === "admin") {
      newItem.customer =
        customers?.find((c) => c.customerId === sample.customerId)?.customer ||
        "N/A";
    }

    if (sample.colourId) {
      let c = getColour(sample.colourId);
      newItem.colour = c;
      if (c?.id.includes("02") && c?.label !== "Light Grey") {
        newItem.colourName = `Light ${c?.label}`;
      } else {
        newItem.colourName = c?.label;
      }
    }

    if (sample.texture && Object.keys(sample.texture).length !== 0) {
      let texture = getTexture(sample.texture);
      newItem.texture = texture;
      newItem.textureName =
        texture.label === "Other"
          ? `${texture.label} - ${sample.texture.description}`
          : texture.label;
    } else {
      newItem.textureName = undefined;
      newItem.texture = undefined;
    }

    let localDate = DateTime.fromISO(sample.processedDate).setZone("local");
    newItem.processedDate = localDate.toFormat("DDD");
    newItem.time = localDate.toFormat("t");

    // TODO: remove legacy `substance` fallback once old sample format is retired
    newItem.bulkScanFindings = sample.tests
      .filter((test) => test.scanType?.toLowerCase() === "bulk-burst")
      .map((t) =>
        t.rb_findings && t.rb_findings.length > 0
          ? t.rb_findings.map((f) => f.substanceCustomer ?? f.substance)
          : (t.findings ?? []).map((f) => f.substanceCustomer ?? f.substance),
      )
      .flat();
    newItem.bulkScanExport = newItem.bulkScanFindings.join(", ");

    newItem.traceScanFindings = sample.tests
      .filter((test) => test.scanType?.toLowerCase() === "trace-burst")
      .map((t) =>
        t.rb_findings && t.rb_findings.length > 0
          ? t.rb_findings.map((f) => f.substanceCustomer ?? f.substance)
          : (t.findings ?? []).map((f) => f.substanceCustomer ?? f.substance),
      )
      .flat();
    newItem.traceScanExport = newItem.traceScanFindings.join(", ");

    newItem._id = sample._id;
    newItem.sampleId = sample.sampleId;
    newItem.customId = sample.customId;

    const location = locations?.find(
      (loc) => loc.locationId === sample.locationId,
    );
    newItem.location =
      user.role === "admin"
        ? customers
            .find((c) => c.customerId === sample.customerId)
            ?.locations?.find((loc) => loc.locationId === sample.locationId)
            ?.name || sample.locationId
        : location
          ? location.name
          : sample.locationId;

    newItem.presumedSubstance =
      sample.presumedSubstance === "Other"
        ? `${sample.presumedSubstance} - ${sample.presumedSubDescription}`
        : sample.presumedSubstance;

    newItem.notes = sample.description;
    newItem.reachbackStatus = sample.reachback?.status ?? "none";
    newItem.reachbackStatusDisplay =
      newItem.reachbackStatus === "none"
        ? "n/a"
        : newItem.reachbackStatus === "inProgress"
          ? "In Progress"
          : "Complete";
    newItem.retestRecommended = sample.reachback?.retestRecommended === true;

    if (customerType === "harm-reduction") {
      newItem.adverseReaction =
        (sample.categories?.length > 0 && !sample.postConsumption) ||
        sample.postConsumption?.adverse
          ? "Yes"
          : "No";
      newItem.tested = sample.postConsumption?.tested || "N/A";
      newItem.consumptionLocation =
        sample.postConsumption?.consumptionLocation || "N/A";
      newItem.behaviourChange = sample.postConsumption?.behaviourChange
        ? sample.postConsumption.behaviourChange
            .map((item) => item.label)
            .join(", ")
        : "N/A";
      newItem.consumptionMethod = sample.postConsumption?.consumptionMethod
        ? sample.postConsumption.consumptionMethod.label
        : "N/A";
      newItem.adverseReactionSymptoms =
        sample.postConsumption?.symptoms?.length > 0 &&
        sample.postConsumption?.adverse === true
          ? sample.postConsumption.symptoms
              .map((symptom) => symptom.label)
              .join(", ")
          : "N/A";
      newItem.adverseReactionIntervention =
        sample.postConsumption?.intervention?.length > 0 &&
        sample.postConsumption?.adverse === true
          ? sample.postConsumption.intervention
              .map((intervention) => intervention.label)
              .join(", ")
          : sample.categories?.length > 0
            ? getAdverse(sample.categories).join(", ")
            : "N/A";
    }

    newItem.otherResults =
      sample.otherResults && sample.otherResults.length > 0
        ? sample.otherResults
            .map((result) => {
              if (result.testType === "testStrip") {
                return `${result.type} Test Strip: ${result.results}`;
              } else if (result.type === "Other") {
                return `${result.type} - ${result.otherTechniqueType}: ${result.results}`;
              } else {
                return `${result.type}: ${result.results}`;
              }
            })
            .join(", ")
        : "N/A";

    return newItem;
  };

  const tableData =
    isLoading || isError ? [] : (recentSamples?.sequences ?? []).map(mapSample);

  useEffect(() => {
    if (locations && locations.length > 1) {
      setSelectedLocation("All");
    } else if (locations && locations.length === 1 && user.role !== "admin") {
      setSelectedLocation(locations[0].locationId);
    }
  }, [locations, user.role]);

  // Body templates
  const colourBodyTemplate = (rowData) =>
    rowData.colour?.id ? (
      <ColourCircleC data={getColour(rowData.colour.id)} />
    ) : (
      "n/a"
    );

  const textureBodyTemplate = (rowData) =>
    rowData.texture?.id ? <CardC data={getTexture(rowData.texture)} /> : "n/a";

  const bulkScanBodyTemplate = (rowData) => (
    <SequenceD findings={rowData.bulkScanFindings || ["Inconclusive"]} />
  );

  const traceScanBodyTemplate = (rowData) => (
    <SequenceD findings={rowData.traceScanFindings || ["Inconclusive"]} />
  );

  const presumedSubstanceBodyTemplate = (rowData) => {
    const s = rowData.presumedSubstance;
    if (!s) return "n/a";
    if (s.includes("Fentalog") || s.includes("Benzodiazepines")) {
      return s.split(" ")[0];
    }
    return s;
  };

  const customIdBodyTemplate = (rowData) => rowData.customId || "-";

  const reachbackStatusBodyTemplate = (rowData) => (
    <span className='d-flex align-items-center gap-2'>
      {rowData.reachbackStatusDisplay}
      {rowData.retestRecommended && (
        <>
          <i
            id={`retest-icon-${rowData._id}`}
            className='bi bi-arrow-repeat'
            style={{ color: "#e65c00", fontSize: "1rem", flexShrink: 0 }}
          />
          <Tooltip
            target={`#retest-icon-${rowData._id}`}
            content='Retest Recommended'
          />
        </>
      )}
    </span>
  );

  // Dropdown filter options derived from current data
  const toOpts = (arr) =>
    [...new Set(arr.filter(Boolean))]
      .sort()
      .map((v) => ({ label: v, value: v }));

  const customerOptions = toOpts(tableData.map((r) => r.customer));
  const locationOptions = toOpts(tableData.map((r) => r.location));
  const substanceOptions = (() => {
    const map = new Map();
    tableData
      .map((r) => r.presumedSubstance)
      .filter(Boolean)
      .forEach((v) => {
        const key = v.toLowerCase();
        if (!map.has(key)) map.set(key, v);
      });
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([, v]) => ({ label: v, value: v }));
  })();
  const colourOptions = [
    ...new Map(
      tableData
        .filter((r) => r.colour?.id)
        .map((r) => [r.colourName, r.colour]),
    ).entries(),
  ]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([name, colour]) => ({ label: name, value: name, colour }));
  const textureOptions = [
    ...new Map(
      tableData
        .filter((r) => r.texture?.id)
        .map((r) => [
          r.textureName.toLowerCase(),
          { name: r.textureName, texture: r.texture },
        ]),
    ).entries(),
  ]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, { name, texture }]) => ({ label: name, value: name, texture }));
  const bulkScanOptions = toOpts(
    tableData.flatMap((r) => r.bulkScanFindings || []),
  );
  const traceScanOptions = toOpts(
    tableData.flatMap((r) => r.traceScanFindings || []),
  );
  const reachbackOptions = [
    { label: "n/a", value: "n/a" },
    { label: "In Progress", value: "In Progress" },
    { label: "Complete", value: "Complete" },
  ];
  const adverseOptions = [
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ];

  const makeDropdownFilter = (opts) => (options) => (
    <Dropdown
      value={options.value}
      options={opts}
      onChange={(e) => options.filterCallback(e.value, options.index)}
      placeholder='Select...'
      showClear
    />
  );

  const colourFilterElement = (options) => (
    <Dropdown
      value={options.value}
      options={colourOptions}
      onChange={(e) => options.filterCallback(e.value, options.index)}
      placeholder='Select...'
      showClear
      itemTemplate={(opt) => <ColourCircleC data={opt.colour} />}
      valueTemplate={(opt, props) =>
        opt ? (
          <ColourCircleC data={opt.colour} />
        ) : (
          <span>{props.placeholder}</span>
        )
      }
    />
  );

  const textureFilterElement = (options) => (
    <Dropdown
      value={options.value}
      options={textureOptions}
      onChange={(e) => options.filterCallback(e.value, options.index)}
      placeholder='Select...'
      showClear
      itemTemplate={(opt) => <CardC data={opt.texture} />}
      valueTemplate={(opt, props) =>
        opt ? <CardC data={opt.texture} /> : <span>{props.placeholder}</span>
      }
    />
  );

  if (isLoading) {
    return (
      <div className='flex-container'>
        <Navbar signedIn={false} />
        <div
          className='px-sm-3 px-lg-5 py-3 flex-body'
          style={{ marginTop: "79px" }}
        >
          <div
            className='alert  mx-3'
            role='alert'
            style={{ backgroundColor: "#7ECBF7", fontWeight: "bold" }}
          >
            Fetching samples ...
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex-container'>
        <Navbar signedIn={false} />
        <div
          className='px-sm-3 px-lg-5 py-3 flex-body'
          style={{ marginTop: "79px" }}
        >
          <div className='alert alert-danger mx-3' role='alert'>
            Something went wrong, try refreshing the page
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className='flex-container'>
      <Navbar signedIn={false} />
      <div
        className='px-sm-3 px-lg-5 py-3 flex-body'
        style={{ marginTop: "79px" }}
      >
        {isSuccess && (
          <CardA
            title='Recent Samples'
            subtitle='Click on a row to view and edit the sample.'
            justify='start'
          >
            <div className='d-flex mb-3 justify-content-between align-items-end'>
              <div className='d-flex align-items-center gap-2'>
                <DateSelector
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                />
                {user.role === "admin"
                  ? selectedCustomer.locations?.length > 1 && (
                      <LocationDropdown
                        locations={selectedCustomer?.locations || []}
                        selectedLocation={selectedLocation}
                        setSelectedLocation={setSelectedLocation}
                      />
                    )
                  : locations?.length > 1 && (
                      <LocationDropdown
                        locations={locations}
                        selectedLocation={selectedLocation}
                        setSelectedLocation={setSelectedLocation}
                      />
                    )}
              </div>
              <div className='d-flex gap-2'>
                {hasActiveFilters && (
                  <button
                    className='btn btn-outline-secondary h-100'
                    onClick={() => setFilters(emptyFilters)}
                  >
                    Clear Filters
                  </button>
                )}
                <CSVLink
                  data={tableData.map(sanitizeRowForCsv)}
                  headers={
                    user.role === "admin"
                      ? headers["admin"]
                      : headers[customerType]
                  }
                  filename={`Samples ${dateRange.start} to ${dateRange.end}.csv`}
                  className='btn btn-outline-primary h-100'
                >
                  Export to CSV
                </CSVLink>
              </div>
            </div>

            {isSuccess && recentSamples.sequences.length === 500 && (
              <div className='alert alert-warning py-2 mb-2' role='alert'>
                Showing the 500 most recent samples. Narrow the date range to see all results.
              </div>
            )}

            <div className='d-flex flex-column flex-grow-1'>
              <DataTable
                value={tableData}
                paginator
                rows={20}
                rowsPerPageOptions={[10, 20, 50]}
                filterDisplay='menu'
                filters={filters}
                onFilter={(e) => setFilters(e.filters)}
                removableSort
                onRowClick={(e) => (window.location = `/sample/${e.data._id}`)}
                rowClassName={(rowData) =>
                  rowData.retestRecommended ? "retest-recommended-row" : ""
                }
                rowHover
                stripedRows
                size='small'
                pt={{
                  root: { className: "d-flex flex-column flex-grow-1" },
                  wrapper: { className: "flex-grow-1" },
                  bodyRow: { style: { cursor: "pointer" } },
                }}
                emptyMessage={
                  <div className='d-flex justify-content-center align-items-center flex-column p-4'>
                    <i className='bi bi-search fs-1'></i>
                    <h5>No samples found</h5>
                    <h6>Please adjust the filter settings.</h6>
                  </div>
                }
              >
                {user.role === "admin" && [
                  <Column
                    key='customer'
                    field='customer'
                    header='Customer'
                    filter
                    filterMatchMode='equals'
                    filterElement={makeDropdownFilter(customerOptions)}
                    showFilterMatchModes={false}
                    sortable
                  />,
                  <Column
                    key='location'
                    field='location'
                    header='Location'
                    filter
                    filterMatchMode='equals'
                    filterElement={makeDropdownFilter(locationOptions)}
                    showFilterMatchModes={false}
                    sortable
                  />,
                ]}
                <Column
                  field='sampleId'
                  header='Sample ID'
                  filter
                  filterMatchMode='contains'
                  filterPlaceholder='Search...'
                  sortable
                />
                <Column
                  field='customId'
                  header='Custom ID'
                  body={customIdBodyTemplate}
                  filter
                  filterMatchMode='contains'
                  filterPlaceholder='Search...'
                  sortable
                />
                <Column field='processedDate' header='Date' sortable />
                <Column field='time' header='Time' sortable />
                <Column
                  field='presumedSubstance'
                  header='Presumed Substance'
                  body={presumedSubstanceBodyTemplate}
                  filter
                  filterMatchMode='equalsIgnoreCase'
                  filterElement={makeDropdownFilter(substanceOptions)}
                  showFilterMatchModes={false}
                  sortable
                />
                <Column
                  field='colourName'
                  header='Colour'
                  body={colourBodyTemplate}
                  filter
                  filterMatchMode='equals'
                  filterElement={colourFilterElement}
                  showFilterMatchModes={false}
                  sortable
                />
                <Column
                  field='textureName'
                  header='Texture'
                  body={textureBodyTemplate}
                  filter
                  filterMatchMode='equalsIgnoreCase'
                  filterElement={textureFilterElement}
                  showFilterMatchModes={false}
                  sortable
                />
                <Column
                  field='bulkScanExport'
                  header='Bulk Scan'
                  body={bulkScanBodyTemplate}
                  filter
                  filterMatchMode='contains'
                  filterElement={makeDropdownFilter(bulkScanOptions)}
                  showFilterMatchModes={false}
                  sortable
                />
                <Column
                  field='traceScanExport'
                  header='Trace Scan'
                  body={traceScanBodyTemplate}
                  filter
                  filterMatchMode='contains'
                  filterElement={makeDropdownFilter(traceScanOptions)}
                  showFilterMatchModes={false}
                  sortable
                />
                <Column
                  field='reachbackStatusDisplay'
                  header='Reachback Status'
                  body={reachbackStatusBodyTemplate}
                  filter
                  filterMatchMode='equals'
                  filterElement={makeDropdownFilter(reachbackOptions)}
                  showFilterMatchModes={false}
                  sortable
                />
                {customerType === "harm-reduction" && (
                  <Column
                    field='adverseReaction'
                    header='Adverse Reaction'
                    filter
                    filterMatchMode='equals'
                    filterElement={makeDropdownFilter(adverseOptions)}
                    showFilterMatchModes={false}
                    sortable
                  />
                )}
              </DataTable>
            </div>
          </CardA>
        )}
      </div>
      <Footer />
    </div>
  );
}
